interface TurnstileWidgetOptions {
  sitekey: string;
  action: string;
  theme: "dark" | "light" | "auto";
  size: "normal" | "flexible" | "compact";
  appearance: "always" | "execute" | "interaction-only";
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
}

interface TurnstileApi {
  render(target: string | HTMLElement, options: TurnstileWidgetOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

function isSuccessPayload(value: unknown): value is { ok: true } {
  return (
    typeof value === "object" && value !== null && (value as Record<string, unknown>).ok === true
  );
}

export function initializeWaitlistForm(
  document: Document,
  fetchImpl: typeof fetch = globalThis.fetch.bind(globalThis),
): () => void {
  const form = document.getElementById("waitlist-form") as HTMLFormElement | null;
  const emailInput = document.getElementById("waitlist-email") as HTMLInputElement | null;
  const container = document.getElementById("waitlist-turnstile");
  const status = document.getElementById("waitlist-status");
  const retry = document.getElementById("waitlist-retry");
  const button = form?.querySelector<HTMLButtonElement>('button[type="submit"]') ?? null;
  const view = document.defaultView;
  if (!form || !emailInput || !container || !status || !button || !view) return () => undefined;

  status.tabIndex = -1;
  button.disabled = true;
  let token = "";
  let widgetId: string | undefined;
  let submitting = false;
  let completed = false;
  let disposed = false;
  let verificationMessage = false;
  let script: HTMLScriptElement | null = null;
  let verificationStarted = false;
  let loadTimer: number | undefined;
  let requestTimer: number | undefined;
  let requestController: AbortController | undefined;
  let observer: IntersectionObserver | undefined;
  const sitekey = container.dataset.sitekey || __TURNSTILE_SITE_KEY__;

  const setStatus = (message: string, focus = false, verification = false): void => {
    if (disposed) return;
    verificationMessage = verification;
    status.textContent = message;
    if (focus) status.focus();
  };
  const clearToken = (): void => {
    token = "";
    button.disabled = true;
  };
  const clearLoadTimer = (): void => {
    view.clearTimeout(loadTimer);
    loadTimer = undefined;
  };
  const verificationFailed = (): void => {
    if (disposed || completed || submitting) return;
    clearLoadTimer();
    clearToken();
    setStatus("We couldn't load the security check. Please try verification again.", false, true);
    if (retry) retry.hidden = false;
  };
  const renderWidget = (): void => {
    if (disposed || completed || !sitekey || widgetId !== undefined) return;
    clearLoadTimer();
    if (!view.turnstile) {
      verificationFailed();
      return;
    }
    if (retry) retry.hidden = true;
    try {
      widgetId = view.turnstile.render(container, {
        sitekey,
        action: "waitlist",
        theme: "dark",
        size: "flexible",
        appearance: "interaction-only",
        callback: (nextToken) => {
          if (disposed || completed) return;
          token = nextToken;
          button.disabled = submitting;
          if (retry) retry.hidden = true;
          if (verificationMessage && !submitting) setStatus("");
        },
        "expired-callback": () => {
          if (disposed || completed || submitting) return;
          clearToken();
          setStatus("The security check expired. Please complete it again.", false, true);
        },
        "error-callback": verificationFailed,
      });
    } catch {
      verificationFailed();
    }
  };

  const detachScriptListeners = (): void => {
    script?.removeEventListener("load", renderWidget);
    script?.removeEventListener("error", verificationFailed);
  };
  const loadVerification = (): void => {
    if (disposed || completed || !sitekey || verificationStarted) return;
    verificationStarted = true;
    observer?.disconnect();
    if (view.turnstile) {
      renderWidget();
      return;
    }
    setStatus("Preparing the security check…", false, true);
    if (retry) retry.hidden = true;
    script = document.querySelector<HTMLScriptElement>("#turnstile-api");
    if (!script) {
      script = document.createElement("script");
      script.id = "turnstile-api";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.querySelector<HTMLHeadElement>("head")?.appendChild(script);
    }
    script.addEventListener("load", renderWidget);
    script.addEventListener("error", verificationFailed);
    loadTimer = view.setTimeout(verificationFailed, 15_000);
  };
  const retryVerification = (): void => {
    if (disposed || completed || submitting) return;
    if (retry) retry.hidden = true;
    setStatus("Preparing the security check…", false, true);
    if (widgetId !== undefined && view.turnstile) {
      view.turnstile.reset(widgetId);
      return;
    }
    detachScriptListeners();
    script?.remove();
    script = null;
    verificationStarted = false;
    loadVerification();
  };

  const submit = (event: SubmitEvent): void => {
    event.preventDefault();
    if (disposed || submitting || completed) return;
    emailInput.value = emailInput.value.trim();
    if (!emailInput.checkValidity()) {
      emailInput.setAttribute("aria-invalid", "true");
      setStatus("Enter a valid email address.");
      emailInput.focus();
      return;
    }
    if (!token) {
      loadVerification();
      if (sitekey)
        setStatus("Complete the security check before joining the waitlist.", true, true);
      return;
    }
    submitting = true;
    emailInput.removeAttribute("aria-invalid");
    form.setAttribute("aria-busy", "true");
    button.disabled = true;
    setStatus("Joining the waitlist…");
    const submittedToken = token;
    requestController = new AbortController();
    const signal = requestController.signal;
    requestTimer = view.setTimeout(() => requestController?.abort(), 15_000);
    void (async () => {
      try {
        const response = await fetchImpl("/api/waitlist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailInput.value, turnstileToken: submittedToken }),
          signal,
        });
        const payload: unknown = await response.json().catch(() => null);
        if (disposed) return;
        if (response.ok && isSuccessPayload(payload)) {
          completed = true;
          button.textContent = "You're on the list";
          setStatus("You're on the waitlist. We'll share Veracio launch updates here.", true);
          view.dispatchEvent(new Event("marketing:waitlist-success"));
          return;
        }
        if (response.status === 403) {
          setStatus("The security check could not be verified. Please complete it again.", true);
          return;
        }
        setStatus("We couldn't join the waitlist right now. Try again in a moment.", true);
      } catch {
        setStatus("We couldn't join the waitlist right now. Try again in a moment.", true);
      } finally {
        view.clearTimeout(requestTimer);
        requestController = undefined;
        if (!disposed) {
          submitting = false;
          form.removeAttribute("aria-busy");
          clearToken();
          if (widgetId !== undefined) view.turnstile?.reset(widgetId);
        }
      }
    })();
  };
  const clearEmailError = (): void => {
    emailInput.removeAttribute("aria-invalid");
  };
  form.addEventListener("submit", submit);
  form.addEventListener("focusin", loadVerification);
  emailInput.addEventListener("input", clearEmailError);
  retry?.addEventListener("click", retryVerification);

  if (!sitekey) {
    setStatus("Waitlist signup is temporarily unavailable. Please try again later.");
  } else if (view.turnstile || !view.IntersectionObserver) {
    loadVerification();
  } else {
    observer = new view.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadVerification();
      },
      { rootMargin: "600px" },
    );
    observer.observe(form);
  }

  return () => {
    if (disposed) return;
    disposed = true;
    clearToken();
    clearLoadTimer();
    view.clearTimeout(requestTimer);
    requestController?.abort();
    form.removeAttribute("aria-busy");
    if (widgetId !== undefined) view.turnstile?.remove(widgetId);
    observer?.disconnect();
    form.removeEventListener("submit", submit);
    form.removeEventListener("focusin", loadVerification);
    emailInput.removeEventListener("input", clearEmailError);
    retry?.removeEventListener("click", retryVerification);
    detachScriptListeners();
  };
}
